


























































import { Component, Vue, Ref, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import { firestore } from "firebase/app";
import TimePickerComponent from "@/components/common/TimePicker.vue";
import PickerDialogComponent from "@/components/common/PickerDialog.vue";
import MultiSelectionPickerComponent from "@/components/common/MultiSelectionPicker.vue";
import { formatDayOfTheWeek } from "@/utils/DateFormatter";
import ConfirmationDialogComponent from "../common/ConfirmationDialog.vue";

interface NightModeParameters {
  nightModeAffectsAllSockets: boolean;
  nightModeActiveDays: number[];
  nightModeStart: firestore.Timestamp | null;
  nightModeEnd: firestore.Timestamp | null;
}

@Component({
  components: {
    "multi-selection-picker": MultiSelectionPickerComponent,
    picker: PickerDialogComponent,
    "base-dialog": BaseDialogComponent,
    "time-picker": TimePickerComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class NightModeParametersComponent extends Vue {
  @Ref("baseDialog")
  baseDialogComponent!: BaseDialogComponent;

  @Ref("confirmation")
  confirmationDialogComponent!: ConfirmationDialogComponent;

  resolve?: (value: NightModeParameters) => void = undefined;
  reject?: (reason: string) => void = undefined;

  isClientConfigSettings = false;

  initialStartTime: firestore.Timestamp = firestore.Timestamp.fromDate(new Date);
  initialEndTime: firestore.Timestamp = firestore.Timestamp.fromDate(new Date);
  checkForTimeChange = false;

  initialNightActiveDays: number[] = [];

  nightModeAffectsAllSockets = false;
  nightModeAffectsAllSocketsOptions = [false, true];
  get nightModeAffectsAllSocketsFormatter() {
    return (nightModeAffectsAllSockets: boolean) =>
      this.$t(
        `comBoxDetail.controlConfig.nightModeSocketsOptions.${
          nightModeAffectsAllSockets 
            ? this.isClientConfigSettings 
              ? "allSocketsAlternate"
              : "allSockets" 
            : this.isClientConfigSettings
              ? "programSocketsAlternate"
              : "programSockets"
        }`
      ).toString();
  }

  nightModeActiveDaysNewSelection: number[] = [];
  nightModeActiveDays: number[] = [];
  defaultNightModeActiveDays: number[] = [0, 1, 2, 3, 4, 5, 6];
  nightModeActiveDaysOptions = [0, 1, 2, 3, 4, 5, 6];

  hasChanged = false;

  onNightModeAffectsAllSocketsOptionChanged(nightModeAffectsAllSockets: boolean) {
    if (nightModeAffectsAllSockets !== this.nightModeAffectsAllSockets) {
      this.hasChanged = true;
    }
  }

  @Watch("nightModeStart")
  onNightModeStartTimeChanged() {
    if (this.checkForTimeChange) {
      if (this.nightModeStart !== this.initialStartTime) {
        this.hasChanged = true;
      }
    }
  }

  @Watch("nightModeEnd")
  onNightModeEndTimeChanged() {
    if (this.checkForTimeChange) {
      if (this.nightModeEnd !== this.initialEndTime) {
        this.hasChanged = true;
      }
    }
  }

  @Watch("nightModeActiveDays")
  onNightModeActiveDaysChanged() {
    
    if (this.checkForTimeChange) {
      if (this.nightModeActiveDays.length > this.initialNightActiveDays.length) {
        this.nightModeActiveDays.forEach((it) => {
          if (!this.initialNightActiveDays.includes(it)) {
            this.hasChanged = true;
          }
        });
      }
      else {
        this.initialNightActiveDays.forEach((it) => {
          if (!this.nightModeActiveDays.includes(it)) {
            this.hasChanged = true;
          }
        });
      }
    }
  }

  get nightModeActiveDaysOptionsFormatter() {
    return (dayOfTheWeek: number) => {
      return formatDayOfTheWeek(this, dayOfTheWeek);
    };
  }

  nightModeStart: firestore.Timestamp | null = null;
  nightModeEnd: firestore.Timestamp | null = null;

  async show(currentNightModeParameters: NightModeParameters, isClientConfigSettings?: boolean) {
    this.baseDialogComponent.show({
      title: this.$t("comBoxDetail.nightModeParameters.title").toString(),
      positiveButtonText: this.$t("common.save").toString(),
      negativeButtonText: this.$t("common.abort").toString(),
      closeOnSave: true,
    });

    const dateStart = new Date();
    dateStart.setHours(22);
    dateStart.setMinutes(0);

    const dateEnd = new Date();
    dateEnd.setHours(7);
    dateEnd.setMinutes(0);

    const isActiveDaysDefined = currentNightModeParameters.nightModeActiveDays.length !== 0;

    this.isClientConfigSettings = isClientConfigSettings ?? false;

    this.nightModeAffectsAllSockets =
      currentNightModeParameters.nightModeAffectsAllSockets;
    this.nightModeActiveDays = 
      isActiveDaysDefined || !isClientConfigSettings 
        ? currentNightModeParameters.nightModeActiveDays 
        : this.defaultNightModeActiveDays;
    this.nightModeStart = currentNightModeParameters.nightModeStart ?? firestore.Timestamp.fromDate(dateStart);
    this.nightModeEnd = currentNightModeParameters.nightModeEnd ?? firestore.Timestamp.fromDate(dateEnd);

    this.initialStartTime = this.nightModeStart;
    this.initialEndTime = this.nightModeEnd;
    this.initialNightActiveDays = this.nightModeActiveDays;
    this.checkForTimeChange = true;

    return new Promise<NightModeParameters>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }

  onSave() {
    if (this.hasChanged) {
      this.$emit("hasChanged", [true]);
    }

    this.resolve &&
      this.resolve({
        nightModeStart: this.nightModeStart,
        nightModeEnd: this.nightModeEnd,
        nightModeAffectsAllSockets: this.nightModeAffectsAllSockets,
        nightModeActiveDays: this.nightModeActiveDays,
      });
  }

  async onCancel() {
    if (this.hasChanged) {
      const result = await this.confirmationDialogComponent.show({
        title: this.$t("common.confirmationDialog.cancelTitle").toString(),
        message: this.$t("common.confirmationDialog.cancelMessage").toString(),
      });

      if (result) {
        this.baseDialogComponent.isShowing = false;
      }
    }
    else {
      this.baseDialogComponent.isShowing = false;
    }
  }
}
